/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { useParams } from "react-router-dom";
import { Spacer, Tab, Tabs, theme } from "@nordcloud/gnui";
import { CostBox } from "~/components/CostBox";
import { useCurrency } from "~/hooks";
import { useDisclosure } from "~/hooks/useDisclosure/useDisclosure";
import { isNotEmpty, isNotNil, noop, CloudAccountStatus } from "~/tools";
import { AccountCredentialsUpdateSidebar } from "~/views/accounts/AccountDetails/AccountCredentialsUpdateSidebar/AccountCredentialsUpdateSidebar";
import { ChargeTypeCostsBar } from "../components/ChargeTypeCostsBar";
import { ClusterUtilization } from "../components/ClusterUtilization";
import { isKubernetesOrOpenshift } from "../utils";
import {
  Details,
  DetailsForm,
  DetailsHeader,
  DetailsNotification,
} from "./Details";
import { Header } from "./Header";
import { EstateRecords } from "./Tabs";
import { Account } from "./types";
import { useCloudAccountDetails } from "./useCloudAccountDetails";

export function CloudAccountDetailsPage() {
  const {
    open: openCredentialsUpdateSteps,
    close: closeCredentialsUpdateSteps,
    isOpen: showCredentialsUpdateSteps,
  } = useDisclosure();

  const { nid } = useParams<RouteParams>();

  const {
    account,
    tags,
    accountName,
    provider,
    providerType,
    ownerData,
    contactPeople,
    accountDescription,
    showUpdateCredentials,
    chargeTypeCosts,
    providerId,
    accountSavingsCosts,
    accountSpends,
  } = useCloudAccountDetails(nid);

  const { currency } = useCurrency();

  let notification = null;
  if (isNotNil(providerType) && isNotNil(account?.cloudStatusV2)) {
    const accountProps: Account = {
      id: account.id,
      name: account.name,
      cloudStatus: account.cloudStatusV2 as CloudAccountStatus,
      provider,
      providerType,
      metadata: account.metadata ?? undefined,
      owner: ownerData ?? undefined,
    };
    notification = (
      <DetailsNotification
        openCredentialsUpdateSteps={openCredentialsUpdateSteps}
        account={accountProps}
      />
    );
  }

  const showClusterUtilization = isNotNil(providerType)
    ? isKubernetesOrOpenshift(providerType)
    : false;

  return (
    <>
      <Header
        tags={tags}
        accountName={accountName}
        account={account}
        openCredentialsUpdateSteps={openCredentialsUpdateSteps}
        showUpdateCredentials={showUpdateCredentials}
      />
      {notification}
      <Details>
        <div css={{ width: "100%" }}>
          <DetailsHeader
            accountName={accountName}
            provider={provider}
            providerType={providerType}
            cloudStatus={
              (account?.cloudStatusV2 as CloudAccountStatus) ||
              CloudAccountStatus.NoAccess
            }
          />
          <DetailsForm
            accountInternalId={nid}
            accountId={providerId}
            accountOwner={ownerData}
            description={accountDescription}
            contactsNames={contactPeople
              .map((contactPerson) => contactPerson.name)
              .join(", ")}
          />
        </div>
        <div css={{ width: "70%", height: "18.5rem" }}>
          <CostBox
            numericalValues={{
              forecast: accountSpends.currentMonthForecastValue,
              currentMonth: accountSpends.currentMonthValue,
              previousMonth: accountSpends.previousMonthValue,
              currency,
              budget: 0,
              cloudWasteCost: accountSavingsCosts.cost,
              co2e: accountSpends.currentMonthCo2e,
              co2eSavings: accountSavingsCosts.co2e,
              originalCost: 0,
              reservedInstanceBenefit: 0,
            }}
          />
        </div>
      </Details>
      <When condition={isNotEmpty(chargeTypeCosts)}>
        <Spacer height={theme.spacing.spacing04} />
        <ChargeTypeCostsBar chargeTypeCosts={chargeTypeCosts} />
      </When>
      <When condition={showClusterUtilization}>
        <Spacer height={theme.spacing.spacing04} />
        <ClusterUtilization nordcloudId={account?.nid} />
      </When>
      <Spacer height={theme.spacing.spacing04} />
      <Tabs handleTab={noop} step={0}>
        <Tab heading="Estate Records" caption="Account estate records">
          <EstateRecords
            providerId={providerId}
            providerType={providerType ?? undefined}
            provider={provider}
          />
        </Tab>
      </Tabs>
      {showUpdateCredentials && (
        <AccountCredentialsUpdateSidebar
          account={account}
          isOpen={showCredentialsUpdateSteps}
          close={closeCredentialsUpdateSteps}
        />
      )}
    </>
  );
}

type RouteParams = {
  nid: string;
};
